import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Calendar, Tag, ArrowLeft, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

interface AgentVersion {
  id: string
  agentName: string
  version: string
  displayName: string
  changelogText: string
  releaseDate: string
  isActive: boolean
}

interface ChangelogModalProps {
  isOpen: boolean
  onClose: () => void
  agentType: string
  initialVersion?: string
}

export const ChangelogModal: React.FC<ChangelogModalProps> = ({
  isOpen,
  onClose,
  agentType,
  initialVersion
}) => {
  const [versions, setVersions] = useState<AgentVersion[]>([])
  const [selectedVersion, setSelectedVersion] = useState<AgentVersion | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch changelog data when modal opens
  useEffect(() => {
    if (isOpen && agentType) {
      fetchChangelog()
    }
  }, [isOpen, agentType])

  // Set initial selected version when versions are loaded
  useEffect(() => {
    if (versions.length > 0 && !selectedVersion) {
      const versionToSelect = initialVersion 
        ? versions.find(v => v.version === initialVersion)
        : versions.find(v => v.isActive) || versions[0]
      
      if (versionToSelect) {
        setSelectedVersion(versionToSelect)
      }
    }
  }, [versions, initialVersion, selectedVersion])

  const fetchChangelog = async () => {
    try {
      setLoading(true)
      setError(null)
      
      console.log(`🔍 Fetching changelog for ${agentType}...`)
      const response = await fetch(`/api/agents/versions/changelog/${agentType.toUpperCase()}`)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch changelog: ${response.statusText}`)
      }
      
      const data = await response.json()
      console.log('📋 Changelog API response:', data)
      setVersions(data.versions || [])
      console.log('📚 Set versions:', data.versions || [])
      
    } catch (err) {
      console.error('Error fetching changelog:', err)
      setError(err instanceof Error ? err.message : 'Failed to load changelog')
      toast.error('Failed to load changelog')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatInlineMarkdown = (text: string) => {
    // Split text by **bold** patterns and format accordingly
    const parts = text.split(/(\*\*.*?\*\*)/g)
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // Bold text
        return (
          <strong key={index} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        )
      }
      return part
    })
  }

  const formatChangelogText = (text: string) => {
    // Convert basic markdown-style formatting to HTML-like structure
    return text
      .split('\n')
      .map((line, index) => {
        const trimmedLine = line.trim()
        
        if (trimmedLine.startsWith('## ')) {
          return <h3 key={index} className="text-lg font-semibold mt-4 mb-2">{trimmedLine.slice(3)}</h3>
        }
        
        if (trimmedLine.startsWith('### ')) {
          return <h4 key={index} className="text-md font-medium mt-3 mb-2">{trimmedLine.slice(4)}</h4>
        }
        
        if (trimmedLine.startsWith('- ')) {
          const listContent = trimmedLine.slice(2)
          return (
            <li key={index} className="ml-4 mb-1">
              {formatInlineMarkdown(listContent)}
            </li>
          )
        }
        
        if (trimmedLine === '') {
          return <br key={index} />
        }
        
        return <p key={index} className="mb-2">{formatInlineMarkdown(trimmedLine)}</p>
      })
  }

  const handleClose = () => {
    setSelectedVersion(null)
    setVersions([])
    setError(null)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            {agentType} Agent Changelog
          </DialogTitle>
          <DialogDescription>
            View release notes and version history for the {agentType} agent
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-6 h-[60vh]">
          {/* Version List Sidebar */}
          <div className="w-64 border-r pr-4">
            <h4 className="font-medium mb-3">Versions</h4>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : error ? (
              <div className="text-sm text-destructive">
                {error}
              </div>
            ) : (
              <ScrollArea className="h-full">
                <div className="space-y-2">
                  {versions.map((version) => (
                    <button
                      key={version.id}
                      onClick={() => setSelectedVersion(version)}
                      className={`
                        w-full text-left p-3 rounded-md border transition-colors
                        ${selectedVersion?.id === version.id 
                          ? 'bg-primary/10 border-primary' 
                          : 'hover:bg-muted border-border'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">v{version.version}</span>
                        {version.isActive && (
                          <Badge variant="default" className="text-xs">Active</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(version.releaseDate)}
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Changelog Content */}
          <div className="flex-1">
            {loading ? (
              <div className="space-y-4">
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : selectedVersion ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold flex items-center gap-2">
                      {selectedVersion.displayName}
                      {selectedVersion.isActive && (
                        <Badge variant="default">Active</Badge>
                      )}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        v{selectedVersion.version}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(selectedVersion.releaseDate)}
                      </span>
                    </div>
                  </div>
                </div>

                <Separator className="mb-4" />

                <ScrollArea className="h-[40vh]">
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    {selectedVersion.changelogText ? (
                      formatChangelogText(selectedVersion.changelogText)
                    ) : (
                      <p className="text-muted-foreground italic">
                        No changelog available for this version.
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </div>
            ) : !error && (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <p>Select a version to view its changelog</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ChangelogModal